import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage({ adminMode = false, developerMode = false }) {
    const navigate = useNavigate();
    const { login, logout } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);


    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email.trim().toLowerCase(), password)

            if (developerMode) {
                if (user.role !== "developer") {
                    await logout(); toast.error("Akun ini bukan akun developer.");
                    navigate("/login/developer");
                    return;
                }
            } else if (adminMode) {
                // Mode admin
                if (!["admin", "super_admin"].includes(user.role)) {
                    await logout(); toast.error("Hanya akun admin yang dapat masuk di halaman ini.");
                    navigate("/login/rahasia-admin");
                    return;
                }
            } else {
                // Mode mitra
                if (user.role !== "mitra") {
                    await logout(); toast.error("Hanya akun mitra yang dapat masuk di halaman ini.");
                    if (["admin", "super_admin", "developer"].includes(user.role)) {
                        navigate("/login");
                    }
                    return;
                }
            }



            toast.success(`Selamat datang, ${user.owner_name}!`);
            navigate(developerMode ? "/developer" : "/dashboard");

        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-5">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <Link to="/" className="flex items-center gap-2.5 mb-8 justify-center" data-testid="back-to-landing">
                    <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
        <img
            src="/images/LG_P4YU.png"
            alt="WarungPay Logo"
            className="w-full h-full object-contain"
        />
    </div>
                    <div>
                        <div className="font-heading font-extrabold text-xl">WARUNGPAY</div>
                    </div>
                </Link>
                <div className={`rounded-3xl bg-card p-7 shadow-xl shadow-brand-deep/5 ${developerMode ? "border border-sky-400/40 shadow-sky-500/10" : adminMode ? "border border-brand-green/20" : "border border-border"}`}>
                    <h1 className="font-heading font-bold text-2xl tracking-tight">
                        {developerMode ? "Masuk ke Developer" : adminMode ? "Masuk ke Admin" : "Masuk ke Mitra"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {developerMode ? "Akses Control Room untuk memantau seluruh aplikasi." : adminMode ? "Masuk dengan akun admin untuk mengelola mitra dan transaksi." : "Lanjutkan transaksi warung Anda."}
                    </p>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                placeholder="warung@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-12 rounded-xl mt-1.5"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">Password</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    id="password"
                                    data-testid="login-password-input"
                                    type={show ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="h-12 rounded-xl pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    data-testid="toggle-password-visibility"
                                >
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={loading}
                            className="w-full bg-brand-green hover:bg-brand-greenDark text-white rounded-xl h-13 min-h-[52px] text-base font-semibold shadow-lg shadow-brand-green/25"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Masuk"}
                        </Button>
                    </form>

                    <div className="text-sm text-center mt-6 text-muted-foreground space-y-1">
                        {developerMode ? (
                            <div><Link to="/login/rahasia-admin" className="font-semibold text-brand-green hover:underline">Masuk sebagai Admin</Link></div>
                        ) : adminMode ? (
                            <div>
                                <Link to="/login" className="font-semibold text-brand-green hover:underline" data-testid="switch-to-mitra-login">
                                    Masuk sebagai Mitra
                                </Link>
                            </div>
                        ) : (
                            <div>
                                Belum punya akun?{" "}
                                <Link to="/register" className="font-semibold text-brand-green hover:underline" data-testid="register-link">
                                    Daftar Mitra Gratis
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

